import type { PurchaseOrder, PurchaseOrderFormData } from '../types/purchaseOrder';
import api from './api';
import { cache } from '../utils/cache';
import { translate } from '../i18n/translate';
import { readLanguagePreferencesFromStorage } from '../features/language/preferences/storage';
import { normalizeListResponse } from '../utils/listResponse';

const t = (key: string, fallback?: string) =>
  translate(readLanguagePreferencesFromStorage().languageCode, key, undefined, fallback);

export interface ListPurchaseOrdersParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  paymentStatus?: string;
  deliveryStatus?: string;
  orderStartDate?: string;
  orderEndDate?: string;
  deliveryStartDate?: string;
  deliveryEndDate?: string;
  supplierId?: string;
}

export const purchaseOrderService = {
  getAll: async (): Promise<PurchaseOrder[]> => {
    return cache.fetch('purchase_orders_all', async () => {
      const response = await api.get('/purchase-orders');
      return response.data.map(transformResponse);
    });
  },

  list: async (params: ListPurchaseOrdersParams = {}): Promise<{ data: PurchaseOrder[]; total: number }> => {
    const { page = 1, size = 25 } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (params.search && params.search.trim()) query.set('search', params.search.trim());
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.paymentStatus && params.paymentStatus !== 'all') query.set('payment_status', params.paymentStatus);
    if (params.deliveryStatus && params.deliveryStatus !== 'all') query.set('delivery_status', params.deliveryStatus);
    if (params.orderStartDate) query.set('order_start_date', params.orderStartDate);
    if (params.orderEndDate) query.set('order_end_date', params.orderEndDate);
    if (params.deliveryStartDate) query.set('delivery_start_date', params.deliveryStartDate);
    if (params.deliveryEndDate) query.set('delivery_end_date', params.deliveryEndDate);
    if (params.supplierId) query.set('supplier_id', params.supplierId);
    const response = await api.get(`/purchase-orders?${query.toString()}`);
    const normalized = normalizeListResponse<unknown>(response.data);
    return {
      data: normalized.data.map((r) => transformResponse(r as Parameters<typeof transformResponse>[0])),
      total: normalized.total,
    };
  },

  getBySupplierId: async (supplierId: string): Promise<PurchaseOrder[]> => {
    return cache.fetch(`purchase_orders_supplier_${supplierId}`, async () => {
      const response = await api.get('/purchase-orders', { params: { supplier_id: supplierId } });
      return response.data.map(transformResponse);
    });
  },

  getById: async (id: string): Promise<PurchaseOrder | undefined> => {
    return cache.fetch(`purchase_orders_${id}`, async () => {
      try {
        const response = await api.get(`/purchase-orders/${id}`);
        return transformResponse(response.data);
      } catch {
        return undefined;
      }
    });
  },

  create: async (data: PurchaseOrderFormData, opts?: { onboardingSample?: boolean }): Promise<PurchaseOrder> => {
    // Generate a PO Number if not provided (simple client-side generation)
    // In a real app, backend usually handles this or we fetch a sequence
    const poNumber = `PO-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`;

    const payload: Record<string, unknown> = {
      supplier_id: data.supplier,
      po_number: poNumber,
      order_date: new Date(data.orderDate).toISOString(),
      expected_delivery_date: data.expectedDeliveryDate ? new Date(data.expectedDeliveryDate).toISOString() : null,
      actual_delivery_date: data.actualDeliveryDate ? new Date(data.actualDeliveryDate).toISOString() : null,
      status: data.status,
      payment_status: data.paymentStatus,
      delivery_status: data.deliveryStatus,
      payment_type: data.paymentType,
      payment_notes: data.paymentNotes,
      payment_proof_url: data.paymentProofUrl ?? null,
      notes: data.notes,
      internal_notes: data.internalNotes,
      expenses: data.purchaseExpenses?.map((expense) => ({
        expense_type: expense.expenseType,
        item_id: expense.itemId,
        item_code: expense.itemCode,
        item_name: expense.itemName,
        quantity_loss: expense.quantityLoss,
        unit_price: expense.unitPrice,
        amount: expense.amount,
        description: expense.description
      })),
      items: data.items.map(item => ({
        item_id: item.inventoryItemId,
        item_code: item.itemCode,
        quantity_ordered: item.quantity,
        quantity_received: item.weightReceived,
        unit_price: item.unitPrice,
        lot_id: item.lotId,
        is_sample: item.sampleItem
      }))
    };

    // When this PO is created during onboarding, flag it so the backend can tag
    // it as a sample record (eligible for cleanup when the tour ends).
    if (opts?.onboardingSample) {
      payload.onboarding_sample = true;
    }

    const response = await api.post('/purchase-orders', payload);
    cache.invalidate('purchase_orders');
    // PO receives create GB lots / increment stock; invalidate inventory so the
    // SO picker and other consumers see the new lots without a manual refresh.
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  update: async (id: string, data: PurchaseOrderFormData, poNumber: string): Promise<PurchaseOrder> => {
    const payload = {
      supplier_id: data.supplier,
      po_number: poNumber,
      order_date: new Date(data.orderDate).toISOString(),
      expected_delivery_date: data.expectedDeliveryDate ? new Date(data.expectedDeliveryDate).toISOString() : null,
      actual_delivery_date: data.actualDeliveryDate ? new Date(data.actualDeliveryDate).toISOString() : null,
      status: data.status,
      payment_status: data.paymentStatus,
      delivery_status: data.deliveryStatus,
      payment_type: data.paymentType,
      payment_notes: data.paymentNotes,
      payment_proof_url: data.paymentProofUrl ?? null,
      notes: data.notes,
      internal_notes: data.internalNotes,
      expenses: data.purchaseExpenses?.map((expense) => ({
        expense_type: expense.expenseType,
        item_id: expense.itemId,
        item_code: expense.itemCode,
        item_name: expense.itemName,
        quantity_loss: expense.quantityLoss,
        unit_price: expense.unitPrice,
        amount: expense.amount,
        description: expense.description
      })),
      items: data.items.map(item => ({
        item_id: item.inventoryItemId,
        item_code: item.itemCode,
        quantity_ordered: item.quantity,
        quantity_received: item.weightReceived,
        unit_price: item.unitPrice,
        lot_id: item.lotId,
        is_sample: item.sampleItem
      }))
    };

    const response = await api.put(`/purchase-orders/${id}`, payload);
    cache.invalidate('purchase_orders');
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/purchase-orders/${id}`);
    cache.invalidate('purchase_orders');
    cache.invalidate('inventory');
    cache.invalidate('reports_');
  }
};

// Helper to transform backend snake_case to frontend camelCase
interface BackendPurchaseOrder {
  id: string;
  supplier_id: string;
  supplier_name?: string;
  po_number: string;
  order_date?: string;
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  status?: string;
  payment_status?: string;
  delivery_status?: string;
  payment_type?: string;
  payment_notes?: string;
  payment_proof_url?: string | null;
  notes?: string;
  internal_notes?: string;
  expenses?: Array<{
    id?: string;
    expense_type?: string;
    item_id?: string;
    item_code?: string;
    item_name?: string;
    quantity_loss?: number;
    unit_price?: number;
    amount?: number;
    description?: string;
  }>;
  items?: Array<{
    item_id: string;
    item_code?: string;
    item_name?: string;
    quantity_ordered: number;
    quantity_received?: number;
    unit_price: number;
    lot_id?: string;
    lot_number?: string;
    is_sample?: boolean;
    sample_id?: string;
  }>;
  total_amount: number;
  created_at?: string;
  updated_at?: string;
}

const transformResponse = (data: BackendPurchaseOrder): PurchaseOrder => {
  return {
    id: data.id,
    supplier: data.supplier_id, // Map ID back to form field
    supplierName: data.supplier_name,
    orderNumber: data.po_number,
    orderDate: data.order_date ? data.order_date.split('T')[0] : '', // Format YYYY-MM-DD for input
    expectedDeliveryDate: data.expected_delivery_date ? data.expected_delivery_date.split('T')[0] : undefined,
    actualDeliveryDate: data.actual_delivery_date ? data.actual_delivery_date.split('T')[0] : undefined,
    status: (data.status as PurchaseOrder['status']) || 'draft',
    paymentStatus: (data.payment_status as PurchaseOrder['paymentStatus']) || 'unpaid',
    deliveryStatus: (data.delivery_status as PurchaseOrder['deliveryStatus']) || 'pending',
    paymentType: data.payment_type || t('purchaseOrder.filters.options.bankTransfer'),
    paymentNotes: data.payment_notes,
    paymentProofUrl: data.payment_proof_url ?? null,
    notes: data.notes,
    internalNotes: data.internal_notes,
    purchaseExpenses: data.expenses?.map((expense) => ({
      expenseType: expense.expense_type || 'po_weight_loss',
      itemId: expense.item_id,
      itemCode: expense.item_code,
      itemName: expense.item_name,
      quantityLoss: expense.quantity_loss || 0,
      unitPrice: expense.unit_price || 0,
      amount: expense.amount || 0,
      description: expense.description,
    })),
    items: (data.items || []).map((item) => ({
      inventoryItemId: item.item_id,
      itemCode: item.item_code,
      name: item.item_name || t('purchaseOrder.form.values.unknownItem'),
      quantity: item.quantity_ordered,
      weightReceived: item.quantity_received,
      unitPrice: item.unit_price,
      lotId: item.lot_id,
      lotNumber: item.lot_number,
      sampleItem: item.is_sample || false,
      sampleId: item.sample_id,
    })),
    totalAmount: data.total_amount,
    createdAt: data.created_at || '',
    updatedAt: data.updated_at || ''
  };
};
